import type { AuditObjectAuditSummary, DriverActivity } from "@ots-frontend/ots-data";
import { useMemo } from "react";
import {
  type ActivityViewFilter,
  buildDriverActivity,
  filterDriverActivity,
  countedRateModels,
  dayRateModelOptions,
  flattenEntries,
  mergeRateModelOptions,
  rateModelsNarrowed,
  summarizeDriverActivity,
  type ActivityRateModelOption,
} from "../driver-activity-view-model";
import type { DriverActivityDisplayOptions } from "../DriverActivityToolbar";
import { activityFacetOptions, type ActivityFacets } from "../model/activity-facets";

export type UseActivityDayOptions = {
  rows?: DriverActivity[];
  audits?: AuditObjectAuditSummary[];
  display: DriverActivityDisplayOptions;
  query: string;
  view: ActivityViewFilter;
  facets?: ActivityFacets;
  /** Drivers folded away; their rows are skipped by the keyboard. */
  collapsed: ReadonlySet<string>;
  now: number;
  scadaHighPercent?: number;
  scadaLowPercent?: number;
  /** Rate models to offer beside the day's own, such as the region's. */
  rateModelOptions?: ActivityRateModelOption[];
};

/** The day as the workspace shows it: built from the rows, summarized, and narrowed by search, view, and display. */
export const useActivityDay = ({ rows, audits, display, query, view, facets, collapsed, now, scadaHighPercent, scadaLowPercent, rateModelOptions: extraRateModels }: UseActivityDayOptions) => {
  // Revenue and cost come from the picked rate models, or the hauling ones until something is picked.
  const rateModelOptions = useMemo(() => mergeRateModelOptions(dayRateModelOptions(rows), extraRateModels), [rows, extraRateModels]);
  const counted = useMemo(() => countedRateModels(display.rateModels, rateModelOptions), [display.rateModels, rateModelOptions]);
  const narrowed = rateModelsNarrowed(counted, rateModelOptions);
  const countedSet = useMemo(() => narrowed ? new Set(counted) : undefined, [narrowed, counted]);
  const auditsById = useMemo(() => audits && new Map(audits.map((audit) => [audit.target.objectId, audit])), [audits]);
  const drivers = useMemo(
    () => buildDriverActivity(rows, {
      markScada: display.markScada,
      markDemurrage: display.markDemurrage,
      audits: auditsById,
      scadaHighPercent,
      scadaLowPercent,
      rateModelNames: countedSet,
      now,
    }),
    [rows, display.markScada, display.markDemurrage, countedSet, auditsById, scadaHighPercent, scadaLowPercent, now]
  );
  const stats = useMemo(() => summarizeDriverActivity(drivers), [drivers]);
  const visible = useMemo(
    () => filterDriverActivity(drivers, {
      query,
      view,
      showVisits: display.showVisits,
      showDispatches: display.showDispatches,
      showSiteWork: display.showSiteWork,
      showTankDrops: display.showTankDrops,
      facets,
    }, now),
    [drivers, query, view, facets, display.showVisits, display.showDispatches, display.showSiteWork, display.showTankDrops, now]
  );
  // The filters offer only what the day has.
  const facetOptions = useMemo(() => activityFacetOptions(drivers), [drivers]);
  const entries = useMemo(() => flattenEntries(drivers), [drivers]);
  const navigable = useMemo(() => flattenEntries(visible.filter((driver) => !collapsed.has(driver.key))), [visible, collapsed]);

  return {
    drivers, visible, stats, entries, navigable, facetOptions, hasRows: drivers.length > 0,
    rateModelOptions, countedRateModels: counted, rateModelsNarrowed: narrowed,
  };
};
